import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layers, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { Navigate } from "react-router";
import { useAdminAuth, canManageAllEvents } from "../auth/AdminAuthContext";
import { tryAdminEventsList } from "../../services/adminEventsService";
import { apiRequest, formatApiError } from "../../api/client";
import type { Event } from "../../types";

type EventGroup = {
  id: string;
  title: string;
  eventIds: string[];
  events: Array<{ id: string; title: string }>;
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "";
  const fmt = (d: Date) =>
    d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  return `${fmt(s)} — ${fmt(e)} ${e.getFullYear()}`;
}

export function AdminEventGroupsPage() {
  const { user } = useAdminAuth();
  const [groups, setGroups] = useState<EventGroup[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [draftIds, setDraftIds] = useState<Record<string, string[]>>({});

  const eventsById = useMemo(
    () => new Map(events.map((ev) => [ev.id, ev])),
    [events],
  );

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      ),
    [events],
  );

  const load = useCallback(async (keepEditingId?: string | null) => {
    setLoading(true);
    setError("");
    try {
      const [g, list] = await Promise.all([
        apiRequest<{ groups: EventGroup[] }>("/event-groups", { audience: "admin" }),
        tryAdminEventsList(),
      ]);
      setGroups(g.groups);
      setEvents(list ?? []);
      setDraftIds(Object.fromEntries(g.groups.map((x) => [x.id, x.eventIds])));
      if (keepEditingId !== undefined) {
        setEditingId(keepEditingId);
      }
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить группы"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(null);
  }, [load]);

  if (!canManageAllEvents(user)) {
    return <Navigate to="/" replace />;
  }

  const startEdit = (group: EventGroup) => {
    setDraftIds((prev) => ({ ...prev, [group.id]: [...group.eventIds] }));
    setEditingId(group.id);
    setError("");
  };

  const cancelEdit = (group: EventGroup) => {
    setDraftIds((prev) => ({ ...prev, [group.id]: [...group.eventIds] }));
    setEditingId(null);
  };

  const create = async () => {
    if (!newTitle.trim()) return;
    setSavingId("new");
    try {
      const res = await apiRequest<{ group: EventGroup }>("/event-groups", {
        method: "POST",
        body: { title: newTitle.trim() },
        audience: "admin",
      });
      setNewTitle("");
      await load(res.group.id);
    } catch (err) {
      setError(formatApiError(err, "Не удалось создать группу"));
    } finally {
      setSavingId(null);
    }
  };

  const save = async (group: EventGroup) => {
    setSavingId(group.id);
    try {
      await apiRequest(`/event-groups/${group.id}`, {
        method: "PATCH",
        body: { eventIds: draftIds[group.id] ?? [] },
        audience: "admin",
      });
      await load(null);
    } catch (err) {
      setError(formatApiError(err, "Не удалось сохранить"));
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (group: EventGroup) => {
    if (!window.confirm(`Удалить группу «${group.title}»?`)) return;
    setSavingId(group.id);
    try {
      await apiRequest(`/event-groups/${group.id}`, { method: "DELETE", audience: "admin" });
      if (editingId === group.id) setEditingId(null);
      await load(null);
    } catch (err) {
      setError(formatApiError(err, "Не удалось удалить"));
    } finally {
      setSavingId(null);
    }
  };

  const toggle = (groupId: string, eventId: string) => {
    setDraftIds((prev) => {
      const cur = prev[groupId] ?? [];
      const next = cur.includes(eventId) ? cur.filter((id) => id !== eventId) : [...cur, eventId];
      return { ...prev, [groupId]: next };
    });
  };

  const memberRows = (group: EventGroup) => {
    const ids = group.eventIds;
    return ids
      .map((id) => {
        const ev = eventsById.get(id);
        const title = ev?.title ?? group.events.find((e) => e.id === id)?.title ?? id;
        const dates =
          ev?.startDate && ev?.endDate ? formatDateRange(ev.startDate, ev.endDate) : "";
        const city = ev?.city ?? "";
        return { id, title, dates, city, start: ev?.startDate ?? "" };
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Layers size={18} />
        </div>
        <div>
          <h1 className="text-[20px] text-gray-900">Группы выставок</h1>
          <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
            Локальная настройка MVK: в реплику не уходит. Если посетитель входит на одну выставку
            группы (бейдж, билет, QR), он получает доступ ко всем остальным и может переключаться
            в приложении — как на площадке «Синергия».
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Название группы, например Синергия"
          className="flex-1 h-10 rounded-xl border border-gray-200 px-3 text-[13px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") void create();
          }}
        />
        <button
          type="button"
          onClick={() => void create()}
          disabled={savingId === "new" || !newTitle.trim()}
          className="h-10 px-4 rounded-xl bg-indigo-600 text-white text-[13px] flex items-center gap-1.5 disabled:opacity-50"
        >
          {savingId === "new" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Создать
        </button>
      </div>

      {error && <p className="text-[13px] text-red-600 mb-4">{error}</p>}
      {loading ? (
        <Loader2 className="animate-spin text-indigo-500" />
      ) : groups.length === 0 ? (
        <p className="text-[13px] text-gray-400">Пока нет групп.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const isEditing = editingId === g.id;
            const selectedCount = isEditing
              ? (draftIds[g.id] ?? []).length
              : g.eventIds.length;
            const members = memberRows(g);

            return (
              <div key={g.id} className="rounded-2xl border border-gray-100 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[15px] text-gray-900 font-medium">{g.title}</p>
                    <p className="text-[12px] text-gray-400 mt-0.5">
                      {selectedCount === 0
                        ? "Выставки ещё не выбраны"
                        : `${selectedCount} ${
                            selectedCount === 1
                              ? "выставка"
                              : selectedCount < 5
                                ? "выставки"
                                : "выставок"
                          }`}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => cancelEdit(g)}
                          className="h-9 px-3 rounded-lg bg-gray-50 text-gray-600 text-[12px] flex items-center gap-1 border border-gray-200"
                        >
                          <X size={13} />
                          Отмена
                        </button>
                        <button
                          type="button"
                          onClick={() => void save(g)}
                          disabled={savingId === g.id}
                          className="h-9 px-3 rounded-lg bg-indigo-600 text-white text-[12px] flex items-center gap-1"
                        >
                          {savingId === g.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Save size={13} />
                          )}
                          Сохранить
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEdit(g)}
                        className="h-9 px-3 rounded-lg bg-indigo-50 text-indigo-700 text-[12px] flex items-center gap-1"
                      >
                        <Pencil size={13} />
                        Изменить
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void remove(g)}
                      className="h-9 w-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"
                      aria-label="Удалить"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {!isEditing ? (
                  members.length === 0 ? (
                    <p className="mt-3 text-[12px] text-gray-400">
                      Нажмите «Изменить», чтобы выбрать выставки.
                    </p>
                  ) : (
                    <ul className="mt-3 space-y-1.5">
                      {members.map((m) => (
                        <li
                          key={m.id}
                          className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2"
                        >
                          <span className="text-[13px] text-gray-900 leading-snug">{m.title}</span>
                          <span className="text-[11px] text-gray-500 whitespace-nowrap text-right">
                            {m.dates}
                            {m.city ? (
                              <span className="block text-gray-400">{m.city}</span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <div className="mt-3 grid sm:grid-cols-2 gap-1.5 max-h-80 overflow-y-auto">
                    {sortedEvents.map((ev) => {
                      const checked = (draftIds[g.id] ?? []).includes(ev.id);
                      const other = groups.find(
                        (og) =>
                          og.id !== g.id &&
                          (draftIds[og.id] ?? og.eventIds).includes(ev.id),
                      );
                      const dates = formatDateRange(ev.startDate, ev.endDate);
                      return (
                        <label
                          key={ev.id}
                          className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-[12px] cursor-pointer ${
                            checked
                              ? "bg-indigo-50 text-indigo-900"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={() => toggle(g.id, ev.id)}
                          />
                          <span className="min-w-0">
                            <span className="block leading-snug">{ev.title}</span>
                            <span className="block text-[10px] text-gray-500 mt-0.5">
                              {dates}
                              {ev.city ? ` · ${ev.city}` : ""}
                            </span>
                            {other && !checked ? (
                              <span className="block text-[10px] text-amber-600 mt-0.5">
                                уже в «{other.title}»
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
